import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Voice capture with two paths.
 *
 * `speech` is the default: the device transcribes locally through the Web
 * Speech API and only the text is sent. Gemini bills audio at roughly 32 tokens
 * per second, so a one-minute note costs about 1900 input tokens as audio
 * against ~150 as text — and nothing has to be uploaded.
 *
 * `audio` is the fallback for browsers without speech recognition (Firefox
 * today) or when it fails outright. Removing it entirely would take voice away
 * from those users, so it stays.
 */

interface SpeechAlternativeLike {
  transcript: string;
}

interface SpeechResultLike {
  0: SpeechAlternativeLike;
  isFinal: boolean;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: { length: number; [index: number]: SpeechResultLike };
}

interface SpeechRecognitionErrorLike {
  error?: string;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorLike) => void) | null;
  onend: (() => void) | null;
}

type RecognitionConstructor = new () => SpeechRecognitionLike;

declare global {
  interface Window {
    SpeechRecognition?: RecognitionConstructor;
    webkitSpeechRecognition?: RecognitionConstructor;
  }
}

export type RecorderStatus =
  | "idle"
  | "requesting"
  | "recording"
  | "finalizing"
  | "ready"
  | "error";

export type CaptureMode = "speech" | "audio";

export interface CaptureResult {
  transcript: string;
  audio?: Blob;
}

/** Base64 inflates by ~4/3 and the server caps the upload at 7 MB. */
export const MAX_AUDIO_BYTES = 5_000_000;
/** Some mobile browsers never fire `onstop`; the promise still has to settle. */
const STOP_TIMEOUT_MS = 6_000;
/** Silence before the capture closes itself, once something has been said. */
const SILENCE_MS = 3_500;
/** How long before that the countdown becomes visible. */
const SILENCE_HINT_MS = 2_000;
const SILENCE_TICK_MS = 250;
/** Speech recognition ends on its own regularly; restarting keeps one session. */
const MAX_RESTARTS = 20;
/** Nothing heard at all within this window means the capture is not working. */
const NO_SPEECH_TIMEOUT_MS = 12_000;
/** RMS level under which the microphone is considered quiet, in audio mode. */
const SILENCE_LEVEL = 0.06;

export function speechRecognitionSupported(): boolean {
  return Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
}

export function audioRecordingSupported(): boolean {
  return Boolean(navigator.mediaDevices?.getUserMedia) && "MediaRecorder" in window;
}

export function formatAudioSize(bytes: number): string {
  return `${(bytes / 1_000_000).toFixed(1)} Mo`;
}

function supportedMimeType(): string | undefined {
  if (!("MediaRecorder" in window)) return undefined;
  return [
    "audio/webm;codecs=opus",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/webm",
    "audio/ogg;codecs=opus"
  ].find((type) => MediaRecorder.isTypeSupported(type));
}

function permissionMessage(error: unknown): string {
  if (error instanceof DOMException) {
    if (error.name === "NotAllowedError" || error.name === "SecurityError") {
      return "Le microphone est bloqué. Autorisez-le dans les réglages du site, puis réessayez.";
    }
    if (error.name === "NotFoundError") {
      return "Aucun microphone n’a été détecté sur cet appareil.";
    }
    if (error.name === "NotReadableError") {
      return "Le microphone est déjà utilisé par une autre application.";
    }
  }
  return "Impossible d’ouvrir le microphone. Vérifiez l’autorisation du site.";
}

function comparable(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^\p{Letter}\p{Number}\s]/gu, "")
    .replace(/\s+/gu, " ")
    .trim()
    .toLowerCase();
}

/**
 * Joins dictation fragments, refusing to repeat what is already there.
 *
 * Speech engines re-emit a growing version of the phrase being spoken —
 * "comment", "comment tu peux", "comment tu peux m'aider" — and appending each
 * one produced exactly that stutter on screen. When the incoming fragment
 * restates the tail we already hold, it replaces it instead of extending it.
 */
export function joinFragments(previous: string, next: string): string {
  const left = previous.trim();
  const right = next.trim();
  if (!right) return left;
  if (!left) return right;

  const a = comparable(left);
  const b = comparable(right);
  // One is a rewrite of the other: keep the more complete wording.
  if (b.startsWith(a)) return right;
  if (a.endsWith(b) || a.includes(b)) return left;

  // Otherwise drop the part of the new fragment already present at the end of
  // the old one, on a word boundary.
  const rightWords = right.split(/\s+/u);
  for (let take = Math.min(rightWords.length, 12); take > 0; take -= 1) {
    const head = comparable(rightWords.slice(0, take).join(" "));
    if (head && a.endsWith(head)) {
      const rest = rightWords.slice(take).join(" ").trim();
      return rest ? `${left} ${rest}` : left;
    }
  }
  return `${left} ${right}`;
}

/**
 * Dictation is text only, so a long capture costs nothing to upload; the audio
 * fallback stays shorter because every second of it becomes a payload.
 */
export const MAX_DICTATION_SECONDS = 600;
export const MAX_RECORDING_SECONDS = 300;

export function useVoiceCapture(maxDurationSeconds = MAX_DICTATION_SECONDS, autoStopOnSilence = true) {
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [mode, setMode] = useState<CaptureMode>("speech");
  const [transcript, setTranscript] = useState("");
  const [audio, setAudio] = useState<Blob>();
  const [error, setError] = useState("");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [inputLevel, setInputLevel] = useState(0);
  const [silenceCountdown, setSilenceCountdown] = useState<number>();

  const recorderRef = useRef<MediaRecorder | undefined>(undefined);
  const streamRef = useRef<MediaStream | undefined>(undefined);
  const recognitionRef = useRef<SpeechRecognitionLike | undefined>(undefined);
  const audioContextRef = useRef<AudioContext | undefined>(undefined);
  const levelFrameRef = useRef<number | undefined>(undefined);
  const chunksRef = useRef<Blob[]>([]);
  const stopResolverRef = useRef<((audio?: Blob) => void) | undefined>(undefined);
  const stopRef = useRef<() => Promise<CaptureResult>>(async () => ({ transcript: "" }));

  /** Text committed by recognition sessions that have already ended. */
  const baseRef = useRef("");
  /** Final text of the session currently running, rebuilt on every event. */
  const sessionFinalRef = useRef("");
  const interimRef = useRef("");
  const lastActivityRef = useRef(0);
  const heardSpeechRef = useRef(false);
  const stoppingRef = useRef(false);
  const restartsRef = useRef(0);
  const startedAtRef = useRef(0);
  /** Set when the dictation service proves unusable, to stop retrying it. */
  const speechUnavailableRef = useRef(false);

  /** Everything heard so far: earlier sessions, this session, and the tail. */
  const finalText = useCallback(
    () => joinFragments(joinFragments(baseRef.current, sessionFinalRef.current), interimRef.current),
    []
  );

  const markActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    heardSpeechRef.current = true;
    setSilenceCountdown(undefined);
  }, []);

  const releaseStream = useCallback(() => {
    if (levelFrameRef.current !== undefined) {
      window.cancelAnimationFrame(levelFrameRef.current);
      levelFrameRef.current = undefined;
    }
    if (audioContextRef.current) {
      void audioContextRef.current.close();
      audioContextRef.current = undefined;
    }
    setInputLevel(0);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = undefined;
  }, []);

  const stopRecognition = useCallback(() => {
    const recognition = recognitionRef.current;
    recognitionRef.current = undefined;
    if (!recognition) return;
    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;
    try {
      recognition.stop();
    } catch {
      try {
        recognition.abort();
      } catch {
        // The session is already closed.
      }
    }
  }, []);

  /* ---------------------------------------------------------------------- */
  /* Speech recognition                                                     */
  /* ---------------------------------------------------------------------- */

  const startRecognition = useCallback((): boolean => {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) return false;
    const recognition = new Recognition();
    recognition.lang = "fr-FR";
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      // Rebuilt from the whole result list rather than from `resultIndex`:
      // Safari re-reports every result on each event, and trusting the index
      // appended the same words again and again.
      let sessionFinal = "";
      let interim = "";
      for (let index = 0; index < event.results.length; index += 1) {
        const result = event.results[index];
        const text = result?.[0]?.transcript || "";
        if (!text) continue;
        if (result.isFinal) sessionFinal = joinFragments(sessionFinal, text);
        // Provisional results are successive guesses at the *same* words, so the
        // latest one replaces the previous rather than being appended to it.
        else interim = text;
      }
      sessionFinalRef.current = sessionFinal;
      interimRef.current = interim;
      setTranscript(finalText());
      markActivity();
    };

    recognition.onerror = (event) => {
      // `no-speech` and `aborted` are routine; `onend` decides what happens next.
      if (event.error === "no-speech" || event.error === "aborted") return;

      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        setError("La dictée est bloquée. Autorisez le microphone dans les réglages du site.");
        // Stopping here matters: setting a flag and returning used to leave the
        // status on "recording" for ever, with an empty live panel and no way out.
        void stopRef.current();
        return;
      }
      if (event.error === "network" || event.error === "audio-capture") {
        // The dictation service is unreachable. Recording the audio still works,
        // so the next attempt takes that path instead of failing the same way.
        speechUnavailableRef.current = true;
        setError(
          "La dictée de votre navigateur est indisponible. Le prochain essai enregistrera l’audio."
        );
        void stopRef.current();
      }
    };

    recognition.onend = () => {
      if (stoppingRef.current || recognitionRef.current !== recognition) return;
      // The finished session is folded into the base before the next one starts,
      // because the next session rebuilds its own text from an empty list.
      baseRef.current = joinFragments(baseRef.current, sessionFinalRef.current);
      sessionFinalRef.current = "";
      interimRef.current = "";

      // Browsers close the session on their own after a pause or about a
      // minute. Without this restart the transcript silently froze mid-sentence.
      if (restartsRef.current >= MAX_RESTARTS) {
        void stopRef.current();
        return;
      }
      restartsRef.current += 1;
      try {
        recognition.start();
      } catch {
        void stopRef.current();
      }
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
      return true;
    } catch {
      return false;
    }
  }, [finalText, markActivity]);

  /* ---------------------------------------------------------------------- */
  /* Audio fallback                                                         */
  /* ---------------------------------------------------------------------- */

  const startRecorder = useCallback(async (): Promise<boolean> => {
    if (!audioRecordingSupported()) return false;
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1
      }
    });
    streamRef.current = stream;

    try {
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.72;
      const samples = new Uint8Array(analyser.frequencyBinCount);
      source.connect(analyser);
      audioContextRef.current = audioContext;
      const measure = () => {
        analyser.getByteTimeDomainData(samples);
        let energy = 0;
        for (const sample of samples) {
          const normalized = (sample - 128) / 128;
          energy += normalized * normalized;
        }
        const level = Math.min(1, Math.sqrt(energy / samples.length) * 4);
        setInputLevel(level);
        // In audio mode the meter is what tells silence from speech.
        if (level > SILENCE_LEVEL) markActivity();
        levelFrameRef.current = window.requestAnimationFrame(measure);
      };
      measure();
    } catch {
      // Recording still works without a level meter.
    }

    const mimeType = supportedMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorderRef.current = recorder;
    chunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onerror = () => {
      setError("L’enregistrement s’est interrompu. Réessayez dans un endroit plus calme.");
    };
    recorder.onstop = () => {
      const blob =
        chunksRef.current.length > 0
          ? new Blob(chunksRef.current, {
              type: recorder.mimeType || chunksRef.current[0]?.type || "audio/webm"
            })
          : undefined;
      const tooLarge = Boolean(blob && blob.size > MAX_AUDIO_BYTES);
      setAudio(tooLarge ? undefined : blob);
      setStatus(blob?.size && !tooLarge ? "ready" : "error");
      if (!blob?.size) {
        setError("Aucun son n’a été enregistré. Vérifiez le microphone et réessayez.");
      } else if (tooLarge) {
        setError(
          `L’enregistrement est trop lourd (${formatAudioSize(blob.size)}). Découpez-le en plusieurs messages plus courts.`
        );
      }
      releaseStream();
      stopResolverRef.current?.(tooLarge ? undefined : blob);
      stopResolverRef.current = undefined;
    };
    recorder.start(250);
    return true;
  }, [markActivity, releaseStream]);

  /* ---------------------------------------------------------------------- */
  /* Public API                                                             */
  /* ---------------------------------------------------------------------- */

  const start = useCallback(async () => {
    if (status === "recording" || status === "requesting" || status === "finalizing") {
      return false;
    }
    if (!speechRecognitionSupported() && !audioRecordingSupported()) {
      setStatus("error");
      setError(
        "Ce navigateur ne permet ni la dictée ni l’enregistrement. Écrivez votre demande ou essayez Chrome ou Safari à jour."
      );
      return false;
    }

    setStatus("requesting");
    setError("");
    setTranscript("");
    setAudio(undefined);
    setElapsedSeconds(0);
    setSilenceCountdown(undefined);
    // A recorder left over from an earlier audio-mode capture must not be
    // mistaken for the current session.
    recorderRef.current = undefined;
    chunksRef.current = [];
    baseRef.current = "";
    sessionFinalRef.current = "";
    interimRef.current = "";
    heardSpeechRef.current = false;
    stoppingRef.current = false;
    restartsRef.current = 0;
    lastActivityRef.current = Date.now();
    startedAtRef.current = Date.now();

    // Preferred path: the device transcribes and nothing is uploaded. Once the
    // dictation service has proved unreachable, stop asking it.
    if (
      speechRecognitionSupported() &&
      !speechUnavailableRef.current &&
      startRecognition()
    ) {
      setMode("speech");
      setStatus("recording");
      return true;
    }

    try {
      if (!(await startRecorder())) throw new Error("unsupported");
      setMode("audio");
      setStatus("recording");
      return true;
    } catch (caught) {
      releaseStream();
      setStatus("error");
      setError(permissionMessage(caught));
      return false;
    }
  }, [releaseStream, startRecognition, startRecorder, status]);

  const stop = useCallback(async (): Promise<CaptureResult> => {
    if (status !== "recording") return { transcript, audio };
    stoppingRef.current = true;
    setStatus("finalizing");
    setSilenceCountdown(undefined);
    stopRecognition();

    // Speech mode has nothing to finalise: the text is already here.
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      releaseStream();
      // The interim tail is kept: the last words are often still provisional
      // when the person goes quiet, and dropping them truncated the sentence.
      const finalTranscript = finalText();
      baseRef.current = finalTranscript;
      sessionFinalRef.current = "";
      interimRef.current = "";
      setTranscript(finalTranscript);
      setStatus(finalTranscript.trim() || audio?.size ? "ready" : "error");
      if (!finalTranscript.trim() && !audio?.size) {
        setError("Je n’ai rien entendu. Réessayez ou écrivez votre demande.");
      }
      return { transcript: finalTranscript, audio };
    }

    const recorded = await new Promise<Blob | undefined>((resolve) => {
      // A watchdog guarantees the caller is never left awaiting forever when the
      // browser drops `onstop`.
      const watchdog = window.setTimeout(() => {
        if (!stopResolverRef.current) return;
        stopResolverRef.current = undefined;
        releaseStream();
        setStatus("error");
        setError("L’enregistrement n’a pas pu être finalisé. Réessayez ou écrivez votre demande.");
        resolve(undefined);
      }, STOP_TIMEOUT_MS);

      stopResolverRef.current = (blob) => {
        window.clearTimeout(watchdog);
        resolve(blob);
      };
      try {
        recorder.requestData();
      } catch {
        // Some mobile browsers do not support requestData before stop.
      }
      try {
        recorder.stop();
      } catch {
        window.clearTimeout(watchdog);
        stopResolverRef.current = undefined;
        releaseStream();
        setStatus("error");
        setError("L’enregistrement s’est interrompu. Réessayez.");
        resolve(undefined);
      }
    });

    return { transcript: finalText(), audio: recorded };
  }, [audio, finalText, releaseStream, status, stopRecognition, transcript]);

  // Assigned in an effect: writing a ref during render is not allowed.
  useEffect(() => {
    stopRef.current = stop;
  }, [stop]);

  const reset = useCallback(() => {
    stoppingRef.current = true;
    stopRecognition();
    if (recorderRef.current?.state === "recording") {
      try {
        recorderRef.current.stop();
      } catch {
        // Nothing else to clean up.
      }
    }
    recorderRef.current = undefined;
    releaseStream();
    chunksRef.current = [];
    baseRef.current = "";
    sessionFinalRef.current = "";
    interimRef.current = "";
    heardSpeechRef.current = false;
    setStatus("idle");
    setTranscript("");
    setAudio(undefined);
    setError("");
    setElapsedSeconds(0);
    setInputLevel(0);
    setSilenceCountdown(undefined);
  }, [releaseStream, stopRecognition]);

  /** Lets the person correct the dictation before sending. */
  const editTranscript = useCallback((value: string) => {
    baseRef.current = value;
    sessionFinalRef.current = "";
    interimRef.current = "";
    setTranscript(value);
  }, []);

  useEffect(() => {
    if (status !== "recording") return;
    const timer = window.setInterval(() => {
      setElapsedSeconds((current) => {
        const next = current + 1;
        const limit = mode === "audio" ? Math.min(maxDurationSeconds, MAX_RECORDING_SECONDS) : maxDurationSeconds;
        if (next >= limit) void stopRef.current();
        return next;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [maxDurationSeconds, mode, status]);

  // Closes the capture on its own once the person stops talking, so a note ends
  // like a sentence rather than requiring a second tap.
  useEffect(() => {
    if (status !== "recording") return;
    const timer = window.setInterval(() => {
      if (!heardSpeechRef.current) {
        // Nothing has arrived at all. A dictation service that never answers —
        // offline, or blocked — would otherwise leave "Je vous écoute…" on
        // screen for ever with an empty panel.
        if (Date.now() - startedAtRef.current > NO_SPEECH_TIMEOUT_MS) {
          setError(
            "Je n’ai rien capté. Vérifiez le microphone et votre connexion, ou écrivez votre demande."
          );
          void stopRef.current();
        }
        return;
      }
      if (!autoStopOnSilence) return;
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= SILENCE_MS) {
        void stopRef.current();
        return;
      }
      setSilenceCountdown(
        idle >= SILENCE_HINT_MS ? Math.ceil((SILENCE_MS - idle) / 1000) : undefined
      );
    }, SILENCE_TICK_MS);
    return () => window.clearInterval(timer);
  }, [autoStopOnSilence, status]);

  useEffect(
    () => () => {
      stoppingRef.current = true;
      stopRecognition();
      if (recorderRef.current?.state === "recording") {
        try {
          recorderRef.current.stop();
        } catch {
          // Component cleanup.
        }
      }
      releaseStream();
    },
    [releaseStream, stopRecognition]
  );

  return {
    status,
    mode,
    transcript,
    setTranscript: editTranscript,
    audio,
    error,
    elapsedSeconds,
    inputLevel,
    silenceCountdown,
    start,
    stop,
    reset,
    speechRecognitionAvailable: speechRecognitionSupported()
  };
}
